import { useEffect, useMemo, useRef } from 'react';
import { useLocation as useReactRouterLocation } from 'react-router-dom';
import { ROUTES, WORKSPACE_TYPE_ROUTES } from '../helpers';
import { WorkspaceTypeID } from '../lib/api/workspaces';
import { useUserData } from '../modules/GenomeWorkspaceView/LeftView/hooks/useUserData';
import { useLazyGetSessionWorkspaceQuery } from './api';
import { useParsedQueryParams } from './useParsedQueryParams';
import { useWorkspaceNavigate } from './useWorkspaceNavigate';
import { prefetchData } from './useWorkspaces/useSingleWorkspace';

const DEEPLINK_WORKSPACE_TYPE_MAP: Record<string, WorkspaceTypeID> = {
  HCPExplorer: WorkspaceTypeID.HCPExplorer,
  HCPExpansion: WorkspaceTypeID.HCPExpansion,
  Throtle: WorkspaceTypeID.Throtle,
  MedscapeListMatch: WorkspaceTypeID.ListMatching,
  MediaEffectiveness: WorkspaceTypeID.MediaEffectiveness,
  DTC: WorkspaceTypeID.DTC,
};

const buildSearchString = (params: Record<string, string> | undefined): string => {
  if (!params) return '';
  const entries = Object.entries(params).filter(([, v]) => v != null && v !== '');
  if (entries.length === 0) return '';
  return `?${new URLSearchParams(entries).toString()}`;
};

export const useHandleDeeplink = () => {
  const { trigger: getSessionWorkspaceID } = useLazyGetSessionWorkspaceQuery();
  const { navigateToWorkspaceWithId, navigate } = useWorkspaceNavigate();
  const { userData } = useUserData();

  const reactRouterLocation = useReactRouterLocation();

  const searchParams = useParsedQueryParams<Record<string, string>>();

  // Honour window.LOOKER_CONFIG.deeplink (host-supplied navigation intent)
  // before falling back to URL query-string routing.
  const hostDeeplinkAppliedRef = useRef(false);
  useEffect(() => {
    if (hostDeeplinkAppliedRef.current) return;
    const hostDeeplink = typeof window !== 'undefined' ? window.LOOKER_CONFIG?.deeplink : undefined;
    if (!hostDeeplink?.path) return;

    hostDeeplinkAppliedRef.current = true;
    const target = `${hostDeeplink.path}${buildSearchString(hostDeeplink.params)}`;
    if (reactRouterLocation.pathname + reactRouterLocation.search === target) return;
    navigate(target, { state: hostDeeplink.state ?? hostDeeplink.params });
  }, [navigate, reactRouterLocation.pathname, reactRouterLocation.search]);

  const deeplinkRoute = useMemo(() => {
    const workspaceTypeID = DEEPLINK_WORKSPACE_TYPE_MAP[searchParams.workspaceType];
    if (!workspaceTypeID || !(workspaceTypeID in WORKSPACE_TYPE_ROUTES)) {
      return ROUTES.QUERY_PARAMS_TEST;
    }

    return `/link${WORKSPACE_TYPE_ROUTES[workspaceTypeID]}`;
  }, [searchParams]);

  useEffect(() => {
    if (Object.keys(searchParams).length === 0) {
      return;
    }

    // Debug tools own their own params — don't bounce them.
    if (
      reactRouterLocation.pathname === ROUTES.AI_ASSISTANT_DEBUG_TOOL ||
      reactRouterLocation.pathname === ROUTES.QUERY_PARAMS_TEST
    ) {
      return;
    }

    // Already on target — pushing again only duplicates the history entry.
    if (reactRouterLocation.pathname === deeplinkRoute) {
      return;
    }

    // Keep the search string: QueryResolutionGuard reads the params off location.search.
    navigate(`${deeplinkRoute}${reactRouterLocation.search}`, { state: searchParams });
  }, [navigate, deeplinkRoute, searchParams, reactRouterLocation.pathname, reactRouterLocation.search]);

  useEffect(() => {
    const getWorkspaceFromSession = async () => {
      try {
        // TODO: To remove in favour of getting query params from the URL
        const platformSession = window.LOOKER_CONFIG?.platformSessionId ?? null;

        if (platformSession && userData.lookerURL) {
          const sessionWorkspace = await getSessionWorkspaceID({
            platformSessionID: platformSession,
          });

          if (sessionWorkspace?.workspaceId) {
            void prefetchData({
              accountID: Number(userData.acctId),
              workspaceID: sessionWorkspace.workspaceId,
            });

            navigateToWorkspaceWithId(sessionWorkspace?.workspaceId, {
              platformSession,
            });
          }
        }
      } catch {
        return;
      }
    };

    void getWorkspaceFromSession();
  }, [getSessionWorkspaceID, navigateToWorkspaceWithId, userData.acctId, userData.lookerURL]);
};
